import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import GenericTile from '@atlaskit/tile';
import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { useAppContext } from '../../../../common/ui/app-context';
import {
	SearchResult,
	type SearchResultEventArguments,
} from '../../../../common/ui/quick-find/search-dialog-result';
import { onAutocompleteSuggestionSelected } from '../../../../common/utils/quick-find/events/autocomplete-suggestion-selected';
import { type State } from '../../../../common/utils/quick-find/state';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../controllers/quick-find/analytics';
import {
	getActionSubjectId,
	isOpenInNewTabInteraction,
} from '../../../../controllers/quick-find/utils';
import {
	usePrimaryProduct,
	useSearchSessionId,
	useUser,
} from '../../../../controllers/store/bootstrap';
import {
	useQuickFindActions,
	useQuickFindQuery,
	useUpdateRecentQueries,
} from '../../../../controllers/store/quick-find';

import { messages } from './messages';

type Props = {
	result: string;
	state: State;
};

export const AutocompleteSuggestion: ({ result, state }: Props) => React.JSX.Element = ({
	result,
	state,
}: Props) => {
	const intl = useIntl();

	const { generateSearchUrl, queryParams } = useAppContext();

	const [primaryProduct] = usePrimaryProduct();
	const [searchSessionId] = useSearchSessionId();
	const [user] = useUser();
	const updateRecentQueries = useUpdateRecentQueries();

	const { setQuickFindOpen } = useQuickFindActions();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const queryText = useQuickFindQuery();

	const onResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			const newTab = isOpenInNewTabInteraction(event, data);
			if (!newTab) {
				setQuickFindOpen(false);
			}

			if (result && user && user.id && primaryProduct) {
				addRecentSearch({
					value: result,
					userId: user.id,
					productId: primaryProduct,
					key: RECENT_SEARCH_KEY,
				});
				updateRecentQueries?.();
			}

			fireAnalyticsEvent(
				onAutocompleteSuggestionSelected({
					actionSubjectId: getActionSubjectId(state),
					attributes: {
						// NOTE: DO NOT LOG THE QUERY TEXT HERE. The query text is UGC and will immediately be a HOT.
						...commonAttributes,
						queryLength: queryText.length,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			commonAttributes,
			fireAnalyticsEvent,
			nonPrivacySafeAttributes,
			primaryProduct,
			queryText.length,
			result,
			setQuickFindOpen,
			state,
			updateRecentQueries,
			user,
		],
	);

	const mapAutocompleteSuggestionToSearchResult = useCallback(
		(query: string) => {
			const id = query;

			const icon = (
				<GenericTile label="" size="small">
					<SearchIcon
						label={intl.formatMessage(messages.autocompleteSuggestion)}
						color="currentColor"
					/>
				</GenericTile>
			);

			const title = query;

			const rightMetadata = <Box>{intl.formatMessage(messages.autocompleteSuggestion)}</Box>;

			const highlightedRightMetadata = (
				<Inline alignBlock="center" space="space.100">
					<Box>{intl.formatMessage(messages.autocompleteSuggestion)}</Box>
					<ReturnIcon />
				</Inline>
			);

			const url = generateSearchUrl({
				...queryParams,
				text: query,
				search_id: searchSessionId,
			});

			return {
				id,
				icon,
				title,
				url,
				rightMetadata,
				highlightedRightMetadata,
			};
		},
		[intl, generateSearchUrl, queryParams, searchSessionId],
	);

	return (
		<SearchResult
			resultType="autocompleteSuggestions"
			{...mapAutocompleteSuggestionToSearchResult(result)}
			onResultClicked={onResultClicked}
		/>
	);
};
